import { Injectable } from '@angular/core';
import { PartnerTypeEnum, PartnerTypeLabel } from 'src/app/domain/enums/PartnerTypeEnum';
import { PartnerContractStatusService } from './partner-contract-status.service';
import { IContractType, IPartnerContractRole } from 'src/app/domain/registration/interfaces/contract-type.interface';

@Injectable({
    providedIn: 'root',
})
export class ContractTypeResolverService {
    private contractTypes: IContractType[] = [
        {
            type: PartnerTypeEnum.PARTNER,
            label: PartnerTypeLabel.partner,
            icon: 'handshake',
            description: 'Contrato de Parceria Comercial'
        },
        {
            type: PartnerTypeEnum.CONSULTANT,
            label: PartnerTypeLabel.consultant,
            icon: 'support_agent',
            description: 'Contrato de Consultoria'
        },
        {
            type: PartnerTypeEnum.REPRESENTATIVE,
            label: PartnerTypeLabel.representative,
            icon: 'person_pin',
            description: 'Contrato de Representação'
        },
        {
            type: PartnerTypeEnum.COUNTER,
            label: PartnerTypeLabel.counter,
            icon: 'calculate',
            description: 'Contrato de Contabilidade'
        },
        {
            type: PartnerTypeEnum.UNIT,
            label: PartnerTypeLabel.unit,
            icon: 'store',
            description: 'Contrato de Unidade'
        }
    ];

    constructor(private contractStatusService: PartnerContractStatusService) { }

    /**
     * Retorna todos os tipos de contrato disponíveis
     */
    getAllContractTypes(): IContractType[] {
        return this.contractTypes;
    }

    /**
     * Obter tipo de contrato pelo seu identificador
     * @param type O tipo de contrato
     */
    getContractType(type: string): IContractType | undefined {
        return this.contractTypes.find(t => t.type === type);
    }

    /**
     * Retorna os papéis de contrato disponíveis para o parceiro com base nos seus flags
     * @param partner O parceiro para verificação
     */
    getPartnerContractRoles(partner: any): IPartnerContractRole[] {
        if (!partner) return [];

        const roles: IPartnerContractRole[] = [];

        if (partner.isPartner) {
            roles.push(this.createRoleFromType(PartnerTypeEnum.PARTNER));
        }

        if (partner.isConsultant) {
            roles.push(this.createRoleFromType(PartnerTypeEnum.CONSULTANT));
        }

        if (partner.isRepresentative) {
            roles.push(this.createRoleFromType(PartnerTypeEnum.REPRESENTATIVE));
        }

        if (partner.isCounter) {
            roles.push(this.createRoleFromType(PartnerTypeEnum.COUNTER));
        }

        if (partner.isUnit) {
            roles.push(this.createRoleFromType(PartnerTypeEnum.UNIT));
        }

        return roles;
    }

    /**
     * Obtém os papéis de contrato com informação de status de contrato
     * @param partner O parceiro para verificação
     */
    async getPartnerContractRolesWithStatus(partner: any): Promise<IPartnerContractRole[]> {
        if (!partner || !partner.id) return [];

        const roles = this.getPartnerContractRoles(partner);

        // Verificar status de contrato para cada papel
        for (const role of roles) {
            const typeStr = typeof role.type === 'string' ? role.type : String(role.type);
            const status = await this.contractStatusService.hasActiveContract(partner.id, typeStr);

            role.hasActiveContract = status.hasContract;
            role.contractStatus = status.status;
            role.contractId = status.contractId;
        }

        return roles;
    }

    /**
     * Cria um objeto de papel com base no tipo de contrato
     * @param type O tipo de contrato
     */
    private createRoleFromType(type: PartnerTypeEnum): IPartnerContractRole {
        const contractType = this.getContractType(type);
        return {
            type: type,
            label: contractType?.label || type,
            icon: contractType?.icon,
        };
    }
} 